import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaCode, 
  FaUsers, 
  FaChartLine, 
  FaClock, 
  FaCalendarAlt, 
  FaSearch, 
  FaChevronRight, 
  FaCheckCircle,
  FaLaptopCode,
  FaDatabase,
  FaMobileAlt,
  FaFolderOpen,
  FaReact,
  FaNodeJs,
  FaBullhorn
} from "react-icons/fa";
import "./Career.css";

export default function InternshipPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Sample internship listings
  const internships = [
    {
      id: 1,
      title: "React Development Intern",
      department: "Frontend Engineering",
      location: "Nashik / Offline",
      duration: "3-6 months",
      posted: "2 days ago",
      category: "react",
    },
    {
      id: 2,
      title: "Node.js Development Intern",
      department: "Backend Engineering",
      location: "Nashik / Offline",
      duration: "6 months",
      posted: "1 week ago",
      category: "nodejs",
    },
    {
      id: 3,
      title: "Digital Marketing Intern",
      department: "Marketing",
      location: "Nashik / Offline",
      duration: "3 months",
      posted: "3 days ago",
      category: "marketing",
    },
    {
      id: 4,
      title: "React Native Developer Intern",
      department: "Mobile Engineering",
      location: "Nashik / Offline",
      duration: "4-6 months",
      posted: "Just now",
      category: "reactnative",
    },
  ];

  // Helper to render respective brand icon
  const getInternshipIcon = (category) => {
    switch (category) {
      case "react":
        return <FaReact className="react-icon-color" />;
      case "nodejs":
        return <FaNodeJs className="nodejs-icon-color" />;
      case "reactnative":
        return (
          <div className="reactnative-icon-wrapper">
            <FaReact className="reactnative-icon-color" />
            <FaMobileAlt className="internship-tech-mini-icon" />
          </div>
        );
      case "marketing":
        return <FaBullhorn className="marketing-icon-color" />;
      default:
        return <FaCode className="text-secondary" />;
    }
  };

  // Filter internships based on active tab and search query
  const filteredInternships = internships.filter((internship) => {
    const matchesCategory =
      activeTab === "all" || internship.category === activeTab;
    const matchesSearch =
      internship.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      internship.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      internship.location.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Internship program features
  const programFeatures = [
    {
      icon: <FaCode />,
      title: "Hands-on Experience",
      description:
        "Work on real projects that impact our products and customers",
    },
    {
      icon: <FaUsers />,
      title: "Mentorship",
      description:
        "Learn from experienced professionals who are passionate about teaching",
    },
    {
      icon: <FaChartLine />,
      title: "Growth Path",
      description:
        "Potential to convert to full-time roles for exceptional performers",
    },
  ];

  const tabs = [
    { id: "all", label: "All Internships" },
    { id: "react", label: "React Development" },
    { id: "nodejs", label: "Node.js Development" },
    { id: "marketing", label: "Digital Marketing" },
    { id: "reactnative", label: "React Native" },
  ];

  // Framer Motion Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 25, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
    exit: {
      scale: 0.96,
      opacity: 0,
      transition: { duration: 0.2 },
    },
  };

  const fadeUp = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <div className="career-page-wrapper">
      {/* Background blobs for premium decoration */}
      <div className="bg-decorations">
        <div className="decor-blob decor-blob-1"></div>
        <div className="decor-blob decor-blob-2"></div>
        <div className="decor-blob decor-blob-3"></div>
      </div>

      {/* Hero Header Section */}
      <div className="container py-5 mt-5">
        <div className="text-center mt-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="career-title"
          >
            Available Internships
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="career-subtitle"
          >
            Launch your career with hands-on projects, expert mentorship, and real-world tech stack exposure at The Innovative Solutions.
          </motion.p>
        </div>

        {/* Dynamic Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="search-wrapper"
        >
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search role, department or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-field"
            />
          </div>
        </motion.div>

        {/* Liquid Tab Selection Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="tabs-container"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`glass-tab ${activeTab === tab.id ? "active" : ""}`}
            >
              {activeTab === tab.id && (
                <motion.span
                  layoutId="activeTabIndicator"
                  className="active-tab-indicator"
                  transition={{ type: "spring", stiffness: 350, damping: 28 }}
                />
              )}
              <span style={{ position: "relative", zIndex: 1 }}>{tab.label}</span>
            </button>
          ))}
        </motion.div>

        {/* Internship Card List */}
        <div className="row justify-content-center">
          <div className="col-lg-9 col-md-11">
            <motion.div
              layout
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="d-flex flex-column gap-3"
            >
              <AnimatePresence mode="popLayout">
                {filteredInternships.length > 0 ? (
                  filteredInternships.map((internship) => (
                    <motion.div
                      key={internship.id}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      layout
                      className="glass-career-card"
                    >
                      <div className="glass-career-card-content">
                        {/* Icon Container */}
                        <div className="internship-icon-container">
                          {getInternshipIcon(internship.category)}
                        </div>

                        {/* Card Details */}
                        <div className="flex-grow-1">
                          <div className="row align-items-center">
                            <div className="col-md-8">
                              <div className="job-badge-group">
                                <span className="job-badge badge-dept">
                                  {internship.department}
                                </span>
                                <span className="job-badge badge-loc">
                                  {internship.location}
                                </span>
                              </div>
                              <h3 className="card-job-title">{internship.title}</h3>
                              <div className="job-meta">
                                <span className="job-meta-item">
                                  <FaClock className="me-1" />
                                  {internship.duration}
                                </span>
                                <span className="job-meta-item">
                                  <FaCalendarAlt className="me-1" />
                                  Posted {internship.posted}
                                </span>
                              </div>
                            </div>
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                              <motion.a
                                href="https://forms.gle/UG7uMAZg4Zhrs32T6"
                                target="_blank"
                                className="premium-apply-btn btn"
                                rel="noopener noreferrer"
                                whileHover={{ y: -2 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <span>Apply Now</span>
                                <FaChevronRight />
                              </motion.a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <motion.div
                    key="empty"
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="empty-state"
                  >
                    <FaFolderOpen style={{ fontSize: "3rem", color: "#94a3b8", marginBottom: "1rem" }} />
                    <p>No internships found matching your criteria.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Program Features Section */}
      <section className="features-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="text-center mb-5"
          >
            <h2 className="section-title mb-2">Our Internship Program</h2>
            <p className="lead text-secondary">What makes our internship program unique</p>
          </motion.div>

          <motion.div
            className="row g-4 justify-content-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {programFeatures.map((feature, index) => (
              <div key={index} className="col-lg-4 col-md-6">
                <motion.div variants={cardVariants} className="glass-feature-card">
                  <div className="feature-icon-wrapper">
                    {feature.icon}
                  </div>
                  <h3 className="feature-card-title">{feature.title}</h3>
                  <p className="feature-card-desc">{feature.description}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="tech-stack-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="text-center mb-5"
          >
            <h2 className="section-title mb-2">Technologies You'll Work With</h2>
            <p className="lead text-secondary">
              Gain experience with cutting-edge technologies that are in high demand
            </p>
          </motion.div>

          <motion.div
            className="row g-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            {/* React Card */}
            <div className="col-md-6">
              <motion.div variants={cardVariants} className="tech-card">
                <div className="tech-card-header">
                  <FaLaptopCode className="text-primary fs-4 me-2" />
                  <h3 className="tech-card-title">React Development</h3>
                </div>
                <div className="tech-card-body">
                  <ul className="tech-list">
                    <li className="tech-item">
                      <FaCheckCircle />
                      Modern React with Hooks and Functional Components
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      State Management with Redux or Context API
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Component Libraries and Styling Solutions
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Testing with Jest and React Testing Library
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Node Card */}
            <div className="col-md-6">
              <motion.div variants={cardVariants} className="tech-card">
                <div className="tech-card-header">
                  <FaDatabase className="text-primary fs-4 me-2" />
                  <h3 className="tech-card-title">Node.js Development</h3>
                </div>
                <div className="tech-card-body">
                  <ul className="tech-list">
                    <li className="tech-item">
                      <FaCheckCircle />
                      RESTful API Design and Implementation
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Express.js Framework
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Database Integration (MongoDB, PostgreSQL)
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Authentication and Authorization
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Marketing Card */}
            <div className="col-md-6">
              <motion.div variants={cardVariants} className="tech-card">
                <div className="tech-card-header">
                  <FaChartLine className="text-primary fs-4 me-2" />
                  <h3 className="tech-card-title">Digital Marketing</h3>
                </div>
                <div className="tech-card-body">
                  <ul className="tech-list">
                    <li className="tech-item">
                      <FaCheckCircle />
                      SEO and Content Marketing
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Social Media Strategy and Analytics
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Email Marketing Campaigns
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Digital Advertising (Google Ads, Meta Ads)
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* React Native Card */}
            <div className="col-md-6">
              <motion.div variants={cardVariants} className="tech-card">
                <div className="tech-card-header">
                  <FaMobileAlt className="text-primary fs-4 me-2" />
                  <h3 className="tech-card-title">React Native Development</h3>
                </div>
                <div className="tech-card-body">
                  <ul className="tech-list">
                    <li className="tech-item">
                      <FaCheckCircle />
                      Cross-Platform Mobile App Development
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      UI/UX Implementation for Mobile
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      Native Module Integration
                    </li>
                    <li className="tech-item">
                      <FaCheckCircle />
                      App Performance Optimization
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
